import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from './auth'
import { logger } from '@/lib/logger'
import { toast } from '@/components/ui/toast'
import type { RealtimeChannel, RealtimePostgresChangesPayload } from '@supabase/supabase-js'
import type {
  SpotifyConnectionStatus,
  SpotifyIntegration,
  SpotifyNode,
  SpotifyUser,
  SpotifyPlaylist,
  SpotifyTrack,
  SpotifyPlaybackState,
  SpotifyDevice,
  SpotifyError,
  SpotifySearchResponse,
  PlaybackRequest,
  SpotifyConnectResponse,
  SpotifyCallbackResponse,
  SceneSpotifyConfig
} from '@/types'

interface SpotifyMetadata {
  name?: string
  album?: {
    images?: Array<{ url: string }>
  }
  images?: Array<{ url: string }>
  [key: string]: unknown
}

export const useSpotifyStore = defineStore('spotify', () => {
  // State
  const status = ref<SpotifyConnectionStatus>('disconnected')
  const integration = ref<SpotifyIntegration | null>(null)
  const user = ref<SpotifyUser | null>(null)
  const playlists = ref<SpotifyPlaylist[]>([])
  const recentTracks = ref<SpotifyTrack[]>([])
  const currentTrack = ref<SpotifyTrack | null>(null)
  const playbackState = ref<SpotifyPlaybackState | null>(null)
  const devices = ref<SpotifyDevice[]>([])
  const nodes = ref<SpotifyNode[]>([])
  
  // Loading states
  const isConnecting = ref(false)
  const isLoadingPlaylists = ref(false)
  const isLoadingPlayback = ref(false)
  
  // Error handling
  const error = ref<SpotifyError | null>(null)
  const lastError = ref<SpotifyError | null>(null)

  // Getters
  const isConnected = computed(() => status.value === 'connected')
  const hasError = computed(() => status.value === 'error')
  const isLoading = computed(() => 
    isConnecting.value || isLoadingPlaylists.value || isLoadingPlayback.value
  )
  
  const currentDevice = computed(() => 
    devices.value.find(device => device.is_active)
  )
  
  const isPlaying = computed(() => 
    playbackState.value?.is_playing ?? false
  )

  // Utility functions
  const clearError = () => {
    error.value = null
  }

  const setError = (newError: SpotifyError) => {
    error.value = newError
    lastError.value = newError
    status.value = 'error'
  }

  const setStatus = (newStatus: SpotifyConnectionStatus) => {
    status.value = newStatus
    if (newStatus !== 'error') {
      error.value = null
    }
  }

  // API helper function
  const apiCall = async <T>(
    endpoint: string, 
    options: RequestInit = {}
  ): Promise<T> => {
    try {
      // Get the current session
      const { data: { session } } = await supabase.auth.getSession()
      
      if (!session?.access_token) {
        throw new Error('User not authenticated')
      }

      // Map endpoint to Supabase function names
      const functionName = mapEndpointToFunction(endpoint)
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      const functionUrl = `${supabaseUrl}/functions/v1/${functionName}`

      const response = await fetch(functionUrl, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          ...options.headers,
        },
        ...options,
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ 
          error: 'Unknown error', 
          message: response.statusText 
        }))
        throw new Error(errorData.message || errorData.error || 'API request failed')
      }

      return await response.json()
    } catch (err) {
      logger.error(`Spotify API error (${endpoint}):`, err)
      throw err
    }
  }

  // Helper function to map frontend endpoints to Supabase function names
  const mapEndpointToFunction = (endpoint: string): string => {
    if (endpoint === '/connect') return 'spotify-connect'
    if (endpoint === '/disconnect') return 'spotify-disconnect'
    if (endpoint === '/callback') return 'spotify-callback'
    
    // All other API calls go through the spotify-api proxy
    // Remove the leading slash and return the endpoint name for the proxy
    return `spotify-api${endpoint}`
  }

  // Connection management
  const connect = async (): Promise<void> => {
    try {
      isConnecting.value = true
      setStatus('connecting')
      clearError()

      const response = await apiCall<SpotifyConnectResponse>('/connect', {
        method: 'POST'
      })

      // Redirect to Spotify OAuth
      window.location.href = response.authUrl
    } catch (err) {
      setError({
        code: 'CONNECTION_FAILED',
        message: err instanceof Error ? err.message : 'Failed to connect to Spotify',
        timestamp: new Date(),
        action: 'connect'
      })
    } finally {
      isConnecting.value = false
    }
  }

  const disconnect = async (): Promise<void> => {
    try {
      await apiCall('/disconnect', { method: 'DELETE' })
      
      // Clear all state
      integration.value = null
      user.value = null
      playlists.value = []
      recentTracks.value = []
      currentTrack.value = null
      playbackState.value = null
      devices.value = []
      
      setStatus('disconnected')
      resetInitialization() // Allow re-initialization after disconnect
    } catch (err) {
      setError({
        code: 'DISCONNECT_FAILED',
        message: err instanceof Error ? err.message : 'Failed to disconnect from Spotify',
        timestamp: new Date(),
        action: 'disconnect'
      })
    }
  }

  const processCallback = async (code: string, state: string): Promise<void> => {
    try {
      setStatus('connecting')
      clearError()

      const response = await apiCall<SpotifyCallbackResponse>('/callback', {
        method: 'POST',
        body: JSON.stringify({ code, state })
      })

      if (response.success && response.user) {
        user.value = response.user
        setStatus('connected')
        
        // Load user data and playlists
        await Promise.all([
          fetchUserProfile(),
          fetchPlaylists()
        ])
      } else {
        throw new Error('Callback processing failed')
      }
    } catch (err) {
      setError({
        code: 'CALLBACK_FAILED',
        message: err instanceof Error ? err.message : 'Failed to process Spotify callback',
        timestamp: new Date(),
        action: 'callback'
      })
      throw err
    }
  }

  const refreshConnection = async (): Promise<void> => {
    try {
      await fetchUserProfile()
      setStatus('connected')
    } catch {
      setError({
        code: 'REFRESH_FAILED',
        message: 'Failed to refresh connection',
        timestamp: new Date(),
        action: 'refresh'
      })
    }
  }

  // Data fetching
  const fetchUserProfile = async (): Promise<void> => {
    try {
      const userData = await apiCall<SpotifyUser>('/me')
      user.value = userData
    } catch {
      throw new Error('Failed to fetch user profile')
    }
  }

  const fetchPlaylists = async (): Promise<void> => {
    try {
      isLoadingPlaylists.value = true
      const playlistData = await apiCall<{ items: SpotifyPlaylist[] }>('/playlists')
      playlists.value = playlistData.items
    } catch {
      setError({
        code: 'FETCH_PLAYLISTS_FAILED',
        message: 'Failed to fetch playlists',
        timestamp: new Date()
      })
    } finally {
      isLoadingPlaylists.value = false
    }
  }

  const fetchPlaybackState = async (): Promise<void> => {
    try {
      isLoadingPlayback.value = true
      const state = await apiCall<SpotifyPlaybackState>('/currently-playing')
      playbackState.value = state
      currentTrack.value = state.item
    } catch (err) {
      // Don't set error for playback state - it might not be available
      logger.warn('Failed to fetch playback state:', err)
    } finally {
      isLoadingPlayback.value = false
    }
  }

  const fetchDevices = async (): Promise<void> => {
    try {
      const deviceData = await apiCall<{ devices: SpotifyDevice[] }>('/devices')
      devices.value = deviceData.devices
    } catch (err) {
      logger.warn('Failed to fetch devices:', err)
    }
  }

  // Playback control
  const play = async (options?: PlaybackRequest): Promise<void> => {
    try {
      await apiCall('/play', {
        method: 'POST',
        body: JSON.stringify(options || {})
      })
      
      // Refresh playback state after a short delay
      setTimeout(() => fetchPlaybackState(), 500)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to start playback'
      
      // Check for common Spotify errors
      if (errorMessage.includes('No active device') || errorMessage.includes('NO_ACTIVE_DEVICE')) {
        toast({
          title: 'No Spotify Device',
          description: 'Please open Spotify on a device and start playing something first.',
          variant: 'destructive'
        })
      } else {
        toast({
          title: 'Playback Failed',
          description: errorMessage,
          variant: 'destructive'
        })
      }
      
      setError({
        code: 'PLAY_FAILED',
        message: errorMessage,
        timestamp: new Date(),
        action: 'play'
      })
      
      throw err
    }
  }

  const pause = async (): Promise<void> => {
    try {
      await apiCall('/pause', { method: 'POST' })
      setTimeout(() => fetchPlaybackState(), 500)
    } catch {
      setError({
        code: 'PAUSE_FAILED',
        message: 'Failed to pause playback',
        timestamp: new Date(),
        action: 'pause'
      })
    }
  }

  const nextTrack = async (): Promise<void> => {
    try {
      await apiCall('/next', { method: 'POST' })
      setTimeout(() => fetchPlaybackState(), 500)
    } catch {
      setError({
        code: 'NEXT_FAILED',
        message: 'Failed to skip track',
        timestamp: new Date()
      })
    }
  }

  const previousTrack = async (): Promise<void> => {
    try {
      await apiCall('/previous', { method: 'POST' })
      setTimeout(() => fetchPlaybackState(), 500)
    } catch {
      setError({
        code: 'PREVIOUS_FAILED',
        message: 'Failed to go to previous track',
        timestamp: new Date()
      })
    }
  }

  const seek = async (position_ms: number): Promise<void> => {
    try {
      await apiCall('/seek', {
        method: 'POST',
        body: JSON.stringify({ position_ms })
      })
      setTimeout(() => fetchPlaybackState(), 200)
    } catch {
      setError({
        code: 'SEEK_FAILED',
        message: 'Failed to seek',
        timestamp: new Date(),
        action: 'seek'
      })
    }
  }

  const setVolume = async (volume: number): Promise<void> => {
    try {
      await apiCall('/volume', {
        method: 'POST',
        body: JSON.stringify({ volume_percent: volume })
      })
    } catch {
      setError({
        code: 'VOLUME_FAILED',
        message: 'Failed to set volume',
        timestamp: new Date(),
        action: 'volume'
      })
    }
  }

  const setShuffle = async (state: boolean): Promise<void> => {
    try {
      await apiCall('/shuffle', {
        method: 'POST',
        body: JSON.stringify({ state })
      })
      setTimeout(() => fetchPlaybackState(), 500)
    } catch {
      setError({
        code: 'SHUFFLE_FAILED',
        message: 'Failed to set shuffle',
        timestamp: new Date()
      })
    }
  }

  const setRepeat = async (state: 'off' | 'context' | 'track'): Promise<void> => {
    try {
      await apiCall('/repeat', {
        method: 'POST',
        body: JSON.stringify({ state })
      })
      setTimeout(() => fetchPlaybackState(), 500)
    } catch {
      setError({
        code: 'REPEAT_FAILED',
        message: 'Failed to set repeat',
        timestamp: new Date()
      })
    }
  }

  // Search
  const search = async (
    query: string, 
    types: Array<'track' | 'album' | 'artist' | 'playlist'>
  ): Promise<SpotifySearchResponse> => {
    try {
      return await apiCall<SpotifySearchResponse>(
        `/search?q=${encodeURIComponent(query)}&type=${types.join(',')}`
      )
    } catch (err) {
      setError({
        code: 'SEARCH_FAILED',
        message: 'Search failed',
        timestamp: new Date()
      })
      throw err
    }
  }

  // Get track, playlist, album, or artist by ID
  const getTrackOrPlaylist = async (type: 'track' | 'playlist' | 'album' | 'artist', id: string): Promise<SpotifyMetadata> => {
    try {
      let endpoint: string
      switch (type) {
        case 'track':
          endpoint = `/tracks/${id}`
          break
        case 'playlist':
          endpoint = `/playlists/${id}`
          break
        case 'album':
          endpoint = `/albums/${id}`
          break
        case 'artist':
          endpoint = `/artists/${id}`
          break
        default:
          throw new Error(`Unsupported type: ${type}`)
      }
      return await apiCall<SpotifyMetadata>(endpoint)
    } catch {
      setError({
        code: 'FETCH_FAILED',
        message: `Failed to fetch ${type}`,
        timestamp: new Date()
      })
      throw new Error(`Failed to fetch ${type}`)
    }
  }

  // Node management
  const createNode = async (
    name: string, 
    position: { x: number; y: number }
  ): Promise<SpotifyNode> => {
    try {
      const node = await apiCall<SpotifyNode>('/nodes', {
        method: 'POST',
        body: JSON.stringify({
          name,
          position_x: position.x,
          position_y: position.y
        })
      })
      
      nodes.value.push(node)
      return node
    } catch {
      setError({
        code: 'CREATE_NODE_FAILED',
        message: 'Failed to create Spotify node',
        timestamp: new Date()
      })
      throw new Error('Failed to create Spotify node')
    }
  }

  const updateNode = async (
    nodeId: string, 
    updates: Partial<Pick<SpotifyNode, 'name' | 'position_x' | 'position_y' | 'spotify_url'>>
  ): Promise<void> => {
    try {
      const updatedNode = await apiCall<SpotifyNode>(`/nodes/${nodeId}`, {
        method: 'PATCH',
        body: JSON.stringify(updates)
      })
      
      const index = nodes.value.findIndex(n => n.id === nodeId)
      if (index !== -1) {
        nodes.value[index] = updatedNode
      }
    } catch {
      setError({
        code: 'UPDATE_NODE_FAILED',
        message: 'Failed to update Spotify node',
        timestamp: new Date()
      })
    }
  }

  const deleteNode = async (nodeId: string): Promise<void> => {
    try {
      await apiCall(`/nodes/${nodeId}`, { method: 'DELETE' })
      nodes.value = nodes.value.filter(n => n.id !== nodeId)
    } catch {
      setError({
        code: 'DELETE_NODE_FAILED',
        message: 'Failed to delete Spotify node',
        timestamp: new Date()
      })
    }
  }

  // Scene configuration
  const setSceneSpotifyConfig = async (
    sceneId: string, 
    nodeId: string, 
    config: Partial<SceneSpotifyConfig>
  ): Promise<void> => {
    try {
      await apiCall(`/scenes/${sceneId}/spotify`, {
        method: 'POST',
        body: JSON.stringify({
          spotify_node_id: nodeId,
          ...config
        })
      })
    } catch {
      setError({
        code: 'SET_SCENE_CONFIG_FAILED',
        message: 'Failed to set scene Spotify configuration',
        timestamp: new Date()
      })
    }
  }

  const removeSceneSpotifyConfig = async (
    sceneId: string, 
    nodeId: string
  ): Promise<void> => {
    try {
      await apiCall(`/scenes/${sceneId}/spotify/${nodeId}`, {
        method: 'DELETE'
      })
    } catch {
      setError({
        code: 'REMOVE_SCENE_CONFIG_FAILED',
        message: 'Failed to remove scene Spotify configuration',
        timestamp: new Date()
      })
    }
  }

  // Initialize store - with deduplication
  let initializePromise: Promise<void> | null = null
  let isInitialized = false
  
  const initialize = async (): Promise<void> => {
    // Skip if already initialized and connected
    if (isInitialized && status.value === 'connected') {
      logger.debug('[Spotify] Already initialized, skipping')
      return
    }
    
    // Deduplicate concurrent calls
    if (initializePromise) {
      logger.debug('[Spotify] Initialization in progress, waiting...')
      return initializePromise
    }
    
    initializePromise = (async () => {
      try {
        // Check if user is already connected (active integration only)
        const { data: integrationData } = await supabase
          .from('spotify_integrations')
          .select('*')
          .eq('is_active', true)
          .single()

        if (integrationData) {
          integration.value = integrationData
          setStatus('connected')
          isInitialized = true
          
          // Fetch initial data - but don't block initialization
          // These calls each hit the Spotify API via get_spotify_tokens
          Promise.allSettled([
            fetchUserProfile(),
            fetchPlaylists(),
            // Skip these on init - they're less important and cause extra token calls
            // fetchPlaybackState(),
            // fetchDevices()
          ]).catch(err => logger.warn('[Spotify] Initial data fetch failed:', err))
        } else {
          setStatus('disconnected')
          isInitialized = true
        }
      } catch {
        logger.info('No existing Spotify integration found')
        setStatus('disconnected')
        isInitialized = true
      } finally {
        initializePromise = null
      }
    })()
    
    return initializePromise
  }

  // Reset initialization state (for when user disconnects)
  const resetInitialization = () => {
    isInitialized = false
    initializePromise = null
  }

  // Real-time subscriptions
  const subscribeToIntegrations = () => {
    const authStore = useAuthStore()
    if (!authStore.user) return null

    const subscription = supabase
      .channel('spotify-integrations-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'spotify_integrations',
          filter: `user_id=eq.${authStore.user.id}`
        },
        (payload) => {
          handleIntegrationChange(payload as any)
        }
      )
      .subscribe()

    return subscription
  }

  const handleIntegrationChange = (payload: RealtimePostgresChangesPayload<SpotifyIntegration>) => {
    const { eventType, new: newRecord } = payload

    switch (eventType) {
      case 'INSERT':
      case 'UPDATE':
        if (newRecord) {
          // Only set as connected if the integration is active
          if (newRecord.is_active) {
            integration.value = newRecord
            setStatus('connected')
            // Fetch fresh data after connection
            if (eventType === 'INSERT') {
              Promise.allSettled([
                fetchUserProfile(),
                fetchPlaylists(),
                fetchPlaybackState(),
                fetchDevices()
              ])
            }
          } else {
            // Integration was deactivated
            integration.value = null
            user.value = null
            playlists.value = []
            recentTracks.value = []
            currentTrack.value = null
            playbackState.value = null
            devices.value = []
            setStatus('disconnected')
          }
        }
        break
      
      case 'DELETE':
        integration.value = null
        user.value = null
        playlists.value = []
        recentTracks.value = []
        currentTrack.value = null
        playbackState.value = null
        devices.value = []
        setStatus('disconnected')
        break
    }
  }

  const unsubscribeFromIntegrations = (subscription: RealtimeChannel | null) => {
    if (subscription) {
      supabase.removeChannel(subscription)
    }
  }

  // Return state and actions
  return {
    // State
    status: computed(() => status.value),
    integration: computed(() => integration.value),
    user: computed(() => user.value),
    playlists: computed(() => playlists.value),
    recentTracks: computed(() => recentTracks.value),
    currentTrack: computed(() => currentTrack.value),
    playbackState: computed(() => playbackState.value),
    devices: computed(() => devices.value),
    nodes: computed(() => nodes.value),
    
    // Loading states
    isConnecting: computed(() => isConnecting.value),
    isLoadingPlaylists: computed(() => isLoadingPlaylists.value),
    isLoadingPlayback: computed(() => isLoadingPlayback.value),
    
    // Error handling
    error: computed(() => error.value),
    lastError: computed(() => lastError.value),
    
    // Computed
    isConnected,
    hasError,
    isLoading,
    currentDevice,
    isPlaying,
    
    // Actions
    connect,
    disconnect,
    processCallback,
    refreshConnection,
    fetchUserProfile,
    fetchPlaylists,
    fetchPlaybackState,
    fetchDevices,
    play,
    pause,
    nextTrack,
    previousTrack,
    seek,
    setVolume,
    setShuffle,
    setRepeat,
    search,
    getTrackOrPlaylist,
    createNode,
    updateNode,
    deleteNode,
    setSceneSpotifyConfig,
    removeSceneSpotifyConfig,
    clearError,
    setError,
    initialize,
    subscribeToIntegrations,
    handleIntegrationChange,
    unsubscribeFromIntegrations
  }
})